'use client';

import { useEffect, useState } from 'react';

/**
 * Reusable "live sprite transform tester" system.
 *
 * Built for the Glymling splat animation, but not specific to it -- any
 * future animated sprite overlay that needs rotate/flip/scale/nudge tuning
 * (found by eye, not by guessing CSS numbers blind) can reuse this instead
 * of a one-off hand-rolled panel:
 *
 *   const { adjust, setAdjust, save } = useSpriteTuning('my-thing-name', MY_DEFAULT);
 *   // render your sprite with style={{ transform: spriteAdjustToTransform(adjust) }}
 *   {isSuperAdmin && testMode && (
 *     <SpriteTransformTesterPanel
 *       adjust={adjust} onAdjustChange={setAdjust} defaultAdjust={MY_DEFAULT}
 *       frameCount={MY_FRAMES.length} frameIndex={frameIndex} onFrameIndexChange={setFrameIndex}
 *       frameLabel={(i) => `frame${i + 1}`} onSave={save} onExit={() => setTestMode(false)}
 *     />
 *   )}
 *
 * Persistence is generic too -- GET/PUT /api/sprite-tuning/[key], a plain
 * key -> JSON blob store (see supabase/migrations/..._sprite_transform_tuning.sql),
 * not specific to any one sprite's shape.
 */

export type SpriteAdjust = {
  rotateDeg: number;
  flipX: boolean;
  flipY: boolean;
  scale: number;
  offsetX: number;
  offsetY: number;
};

/** Builds the CSS `transform` value for a given adjust state. Nudge (offset)
 * is applied in plain screen-space pixels regardless of rotation/flip, by
 * listing it first (CSS transform functions apply right-to-left, so the
 * leftmost -- this one -- is the last one applied to the already-rotated
 * result). */
export function spriteAdjustToTransform(adjust: SpriteAdjust): string {
  const sx = adjust.flipX ? -adjust.scale : adjust.scale;
  const sy = adjust.flipY ? -adjust.scale : adjust.scale;
  return `translate(${adjust.offsetX}px, ${adjust.offsetY}px) rotate(${adjust.rotateDeg}deg) scale(${sx}, ${sy})`;
}

// Module-level cache so multiple components tuning the same key (or the
// same component remounting) don't each fire their own fetch.
const tuningCache = new Map<string, SpriteAdjust>();

/** Loads any saved tuning for `key` on mount (falling back to `fallback`
 * until/unless one exists), and exposes a `save` that persists the current
 * `adjust` value for every future viewer -- not just this browser tab. */
export function useSpriteTuning(key: string, fallback: SpriteAdjust) {
  const [adjust, setAdjust] = useState<SpriteAdjust>(tuningCache.get(key) ?? fallback);
  const [ready, setReady] = useState(tuningCache.has(key));

  useEffect(() => {
    if (tuningCache.has(key)) return;
    let cancelled = false;
    fetch(`/api/sprite-tuning/${encodeURIComponent(key)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        const loaded = data?.adjust;
        if (loaded && typeof loaded === 'object') {
          tuningCache.set(key, loaded as SpriteAdjust);
          setAdjust(loaded as SpriteAdjust);
        }
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [key]);

  const save = async () => {
    tuningCache.set(key, adjust);
    try {
      await fetch(`/api/sprite-tuning/${encodeURIComponent(key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adjust }),
      });
      return true;
    } catch {
      return false;
    }
  };

  return { adjust, setAdjust, ready, save };
}

interface SpriteTransformTesterPanelProps {
  adjust: SpriteAdjust;
  onAdjustChange: (adjust: SpriteAdjust) => void;
  defaultAdjust: SpriteAdjust;
  /** Total frames in the sequence being previewed. */
  frameCount: number;
  frameIndex: number;
  onFrameIndexChange: (index: number) => void;
  /** Human label for the current frame, e.g. `(i) => "frame" + (i+1)`. */
  frameLabel: (index: number) => string;
  onSave: () => void | Promise<boolean>;
  onExit: () => void;
  /** Shown in the panel header, e.g. "Glym splat". */
  title?: string;
}

export function SpriteTransformTesterPanel({
  adjust,
  onAdjustChange,
  defaultAdjust,
  frameCount,
  frameIndex,
  onFrameIndexChange,
  frameLabel,
  onSave,
  onExit,
  title,
}: SpriteTransformTesterPanelProps) {
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const patch = (p: Partial<SpriteAdjust>) => onAdjustChange({ ...adjust, ...p });
  const step = (delta: number) => onFrameIndexChange((frameIndex + delta + frameCount) % frameCount);
  const btnCls = 'rounded bg-white/15 px-2 py-1 text-xs font-semibold hover:bg-white/25';

  const handleSave = async () => {
    setSaveState('saving');
    await onSave();
    setSaveState('saved');
    setTimeout(() => setSaveState('idle'), 1500);
  };

  return (
    <div className="flex max-w-xs flex-col gap-2 rounded-lg bg-black/85 p-3 text-white shadow-lg">
      {title && <div className="text-xs font-semibold text-white/70">{title}</div>}
      <div className="flex items-center gap-2">
        <button type="button" onClick={() => step(-1)} className={btnCls}>‹ Prev</button>
        <span className="whitespace-nowrap font-mono text-xs">
          Frame {frameIndex + 1}/{frameCount} — {frameLabel(frameIndex)}
        </span>
        <button type="button" onClick={() => step(1)} className={btnCls}>Next ›</button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <button type="button" onClick={() => patch({ rotateDeg: adjust.rotateDeg - 90 })} className={btnCls}>Rotate -90°</button>
        <button type="button" onClick={() => patch({ rotateDeg: adjust.rotateDeg + 90 })} className={btnCls}>Rotate +90°</button>
        <button type="button" onClick={() => patch({ flipX: !adjust.flipX })} className={`${btnCls} ${adjust.flipX ? 'bg-primary/60' : ''}`}>Flip X</button>
        <button type="button" onClick={() => patch({ flipY: !adjust.flipY })} className={`${btnCls} ${adjust.flipY ? 'bg-primary/60' : ''}`}>Flip Y</button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-white/60">Scale</span>
        <button type="button" onClick={() => patch({ scale: Math.max(0.2, Math.round((adjust.scale - 0.1) * 10) / 10) })} className={btnCls}>−</button>
        <span className="font-mono text-xs">{adjust.scale.toFixed(1)}x</span>
        <button type="button" onClick={() => patch({ scale: Math.round((adjust.scale + 0.1) * 10) / 10 })} className={btnCls}>+</button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-white/60">Nudge X</span>
        <button type="button" onClick={() => patch({ offsetX: adjust.offsetX - 5 })} className={btnCls}>−</button>
        <span className="font-mono text-xs">{adjust.offsetX}px</span>
        <button type="button" onClick={() => patch({ offsetX: adjust.offsetX + 5 })} className={btnCls}>+</button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-white/60">Nudge Y</span>
        <button type="button" onClick={() => patch({ offsetY: adjust.offsetY - 5 })} className={btnCls}>−</button>
        <span className="font-mono text-xs">{adjust.offsetY}px</span>
        <button type="button" onClick={() => patch({ offsetY: adjust.offsetY + 5 })} className={btnCls}>+</button>
      </div>
      <div className="rounded bg-white/10 px-2 py-1.5 font-mono text-[10px] leading-snug text-white/80">
        {`rotateDeg: ${adjust.rotateDeg}, flipX: ${adjust.flipX}, flipY: ${adjust.flipY}, scale: ${adjust.scale}, offsetX: ${adjust.offsetX}, offsetY: ${adjust.offsetY}`}
      </div>
      <div className="flex items-center gap-1.5">
        <button type="button" onClick={() => onAdjustChange(defaultAdjust)} className={btnCls}>Reset</button>
        <button type="button" onClick={handleSave} className="rounded bg-emerald-600 px-2 py-1 text-xs font-semibold hover:bg-emerald-500">
          {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Saved ✓' : 'Save'}
        </button>
        <button type="button" onClick={onExit} className="rounded bg-red-600 px-2 py-1 text-xs font-semibold hover:bg-red-500">Exit</button>
      </div>
    </div>
  );
}
